import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Input,
  Dialog,
  Table,
  Badge,
  Field,
  useDisclosure,
  Text,
  Stack,
  IconButton
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../api.js';

const MODULES = ['clients', 'articles', 'transactions'];
const ACTIONS = ['view', 'create', 'edit', 'delete'];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const { open, onOpen, onClose } = useDisclosure(); 

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    permissions: {
      clients: { view: true, create: false, edit: false, delete: false },
      articles: { view: true, create: false, edit: false, delete: false },
      transactions: { view: true, create: false, edit: false, delete: false }
    }
  });

  const fetchUsers = async () => {
    try {
      const res = await apiGet('/users');
      setUsers(res.users);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpen = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        permissions: user.permissions || {
          clients: { view: true, create: false, edit: false, delete: false },
          articles: { view: true, create: false, edit: false, delete: false },
          transactions: { view: true, create: false, edit: false, delete: false }
        }
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        permissions: {
          clients: { view: true, create: false, edit: false, delete: false },
          articles: { view: true, create: false, edit: false, delete: false },
          transactions: { view: true, create: false, edit: false, delete: false }
        }
      });
    }
    onOpen();
  };

  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [action]: !!checked // ensure boolean
        }
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await apiPut(`/users/${editingUser._id}`, {
          permissions: formData.permissions,
          ...(formData.password ? { password: formData.password } : {})
        });
        alert('User updated successfully');
      } else {
        await apiPost('/users', formData);
        alert('User created successfully');
      }
      onClose();
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Error saving user');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiDelete(`/users/${id}`);
      alert('User deleted successfully');
      fetchUsers();
    } catch (err) {
      alert('Error deleting user');
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">User Management</Heading>
        <Button colorPalette="blue" onClick={() => handleOpen()}>Add User</Button>
      </Flex>

      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Username</Table.ColumnHeader>
              <Table.ColumnHeader>Role</Table.ColumnHeader>
              <Table.ColumnHeader>Permissions</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {users.map(user => (
              <Table.Row key={user._id}>
                <Table.Cell fontWeight="bold">{user.username}</Table.Cell>
                <Table.Cell><Badge colorPalette={user.role === 'admin' ? 'purple' : 'green'}>{user.role}</Badge></Table.Cell>
                <Table.Cell>
                  <Flex gap={2} wrap="wrap">
                    {Object.entries(user.permissions || {}).map(([mod, perms]) => (
                      <Badge key={mod} variant="outline" colorPalette="gray">
                        {mod}: {Object.entries(perms).filter(([_, v]) => v).map(([k]) => k).join(', ')}
                      </Badge>
                    ))}
                  </Flex>
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <Button size="sm" mr={2} onClick={() => handleOpen(user)}>Edit</Button>
                  <Button size="sm" colorPalette="red" onClick={() => handleDelete(user._id)}>Delete</Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{editingUser ? 'Edit User' : 'Create User'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Stack gap={4}>
                <Field.Root>
                  <Field.Label>Username</Field.Label>
                  <Input
                    value={formData.username}
                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!editingUser}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Field.Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                  />
                </Field.Root>

                <Heading size="sm" mt={2}>Permissions</Heading>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Module</Table.ColumnHeader>
                      {ACTIONS.map(action => <Table.ColumnHeader key={action} textAlign="center">{action.toUpperCase()}</Table.ColumnHeader>)}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {MODULES.map(module => (
                      <Table.Row key={module}>
                        <Table.Cell fontWeight="medium" textTransform="capitalize">{module}</Table.Cell>
                        {ACTIONS.map(action => (
                          <Table.Cell key={action} textAlign="center">
                            <Checkbox.Root
                              checked={formData.permissions[module]?.[action]}
                              onCheckedChange={(e) => handlePermissionChange(module, action, e.checked)}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control>
                                <Checkbox.Indicator />
                              </Checkbox.Control>
                            </Checkbox.Root>
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Stack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button colorPalette="blue" onClick={handleSubmit}>Save</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default Users;
